import * as React from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { Card, CardBody, CardTitle, Input, Label } from 'reactstrap'

import { getBopReportsProfitAndLoss, selectBopReportsStatus } from 'slices/bopReportsSlice'
import { selectWorkspacesStatus } from 'slices/workspacesSlice'

import BopTable from 'components/common/BopReports/BopTable'

import useBopProfitAndLoss from 'hooks/useBopProfitAndLoss'
import useBopReportsQuery from 'hooks/useBopReportsQuery'

import BopReportsCommon from './BopReportsCommon'

const BopReportsProfitAndLoss: React.FC = () => {
  const [dataType, setDataType] = React.useState<'conjecturedEstimates' | 'actuals'>('conjecturedEstimates')
  const [isCheckedEstimate, setIsCheckedEstimate] = React.useState(true)
  const { queryStart, queryEnd } = useBopReportsQuery()
  const { formatUpdatedAt, processProfitAndLossData } = useBopProfitAndLoss()
  const { workspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { bopReports } = useSelector(selectBopReportsStatus, shallowEqual)
  const workspaceIds = React.useMemo(() => workspaces.map(w => w.workspaceId), [workspaces])
  const dispatch = useDispatch()

  React.useEffect(() => {
    dispatch(getBopReportsProfitAndLoss(queryStart, queryEnd))
  }, [dispatch, queryStart, queryEnd])

  const bopCols = React.useMemo(() => {
    if (!bopReports) {
      return []
    }
    return processProfitAndLossData(bopReports[dataType], bopReports.estimates)
  }, [bopReports, dataType, processProfitAndLossData])

  const handleConjecturedEstimatesChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDataType(e.target.checked ? 'conjecturedEstimates' : 'actuals')
  }, [])

  return (
    <BopReportsCommon selectedWorkspaceIds={workspaceIds}>
      <Card className="mt-2">
        <CardBody className="p-4">
          <CardTitle className="mb-3 fw-bold font-large">損益詳細</CardTitle>

          <div className="d-flex mb-3">
            <div className="form-check me-3">
              <Input
                className="form-check-input"
                id="estimate"
                checked={isCheckedEstimate}
                type="checkbox"
                onChange={e => setIsCheckedEstimate(e.target.checked)}
              />
              <Label className="form-check-label" for="estimate">
                見込表示
              </Label>
            </div>

            <div className="form-check">
              <Input
                className="form-check-input"
                id="conjectured-estimates"
                checked={dataType === 'conjecturedEstimates'}
                type="checkbox"
                onChange={handleConjecturedEstimatesChange}
              />
              <Label className="form-check-label" for="conjectured-estimates">
                見込みを含んだ予測表示
              </Label>
            </div>
          </div>

          <BopTable bopCols={bopCols} isCheckedEstimate={isCheckedEstimate} />

          <div className="d-flex justify-content-end align-self-center pt-4 text-muted">
            <i className="icf-updated pe-1" />
            <small>{formatUpdatedAt(bopReports?.updatedAt)}</small>
          </div>
        </CardBody>
      </Card>
    </BopReportsCommon>
  )
}

export default BopReportsProfitAndLoss
